/**
 * rc147 — Interruptores para pausar los automatismos del motor (dos separados,
 * decisión Alex): facturación automática y cobranza automática.
 *
 * Para migrar un ISP (importar desde MikroWisp) o hacer mantenimiento sin que
 * el motor emita facturas o suspenda servicios sobre datos aún no validados.
 * Cada switch guarda al momento, con confirmación. Mientras alguno esté en
 * pausa, la barra superior muestra un aviso a todo el equipo.
 */
import { useState } from "react";
import { toast } from "sonner";
import { PauseCircle, PlayCircle, Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useConfigByGrupo, useUpdateConfig, type ConfigItem } from "@/features/config/api";
import type { ApiError } from "@/lib/api";

type Cual = "facturacion" | "cobranza";

const META: Record<Cual, { key: string; grupo: string; titulo: string; pausado: string; activo: string; descripcion: string }> = {
  facturacion: {
    key: "facturacion.automatica_pausada",
    grupo: "facturacion",
    titulo: "Facturación automática",
    pausado: "No se emiten facturas automáticas. Las simulaciones siguen funcionando.",
    activo: "El poller emite las facturas a su hora, como siempre.",
    descripcion: "rc147 — Pausa la facturación automática (el poller no emite facturas; \"Ejecutar ahora\" responde 409).",
  },
  cobranza: {
    key: "cobranza.automatica_pausada",
    grupo: "cobranza",
    titulo: "Cobranza automática",
    pausado: "No se marcan vencidas ni se suspenden servicios. La reactivación al registrar un pago sigue.",
    activo: "El poller marca vencidas y suspende a su hora, como siempre.",
    descripcion: "rc147 — Pausa la cobranza automática (no marca vencidas, no suspende, sin recovery). La reactivación por pago sigue.",
  },
};

function asBool(items: ConfigItem[] | undefined, key: string): boolean {
  const v = items?.find((i) => i.key === key)?.value;
  return v === true || v === "true" || v === 1 || v === "1";
}

export function PausaAutomatismosCard() {
  const { data: fact } = useConfigByGrupo("facturacion");
  const { data: cob } = useConfigByGrupo("cobranza");
  const updateMut = useUpdateConfig();
  const qc = useQueryClient();
  const [confirmar, setConfirmar] = useState<{ cual: Cual; pausar: boolean } | null>(null);

  const estado: Record<Cual, boolean> = {
    facturacion: asBool(fact, META.facturacion.key),
    cobranza: asBool(cob, META.cobranza.key),
  };

  async function aplicar() {
    if (!confirmar) return;
    const m = META[confirmar.cual];
    try {
      await updateMut.mutateAsync({ key: m.key, value: confirmar.pausar, value_type: "bool", grupo: m.grupo, descripcion: m.descripcion });
      qc.invalidateQueries({ queryKey: ["automatismos"] });
      toast.success(confirmar.pausar ? `${m.titulo} en pausa` : `${m.titulo} reanudada`, {
        description: confirmar.pausar ? undefined : "Vuelve a correr en su siguiente horario.",
      });
      setConfirmar(null);
    } catch (e) {
      toast.error("No se pudo guardar", { description: (e as ApiError)?.message });
    }
  }

  return (
    <div className="lg:col-span-2 rounded-lg border bg-card p-4">
      <div className="flex items-center gap-2">
        <PauseCircle size={14} className="text-warn" />
        <h3 className="text-sm font-semibold">Pausa de automatismos</h3>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Para migraciones o mantenimiento: detiene el motor sin tocar datos. Al reanudar, cada uno vuelve en su siguiente
        horario (no recupera días atrasados).
      </p>
      <div className="mt-3 grid gap-2 md:grid-cols-2">
        {(Object.keys(META) as Cual[]).map((cual) => {
          const m = META[cual];
          const pausada = estado[cual];
          return (
            <div
              key={cual}
              className={`flex items-start justify-between gap-3 rounded-md border p-3 ${
                pausada ? "border-warn/40 bg-warn/[0.07]" : "border-border"
              }`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-xs font-medium">
                  {m.titulo}
                  <span
                    className={`rounded-full px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider ${
                      pausada ? "bg-warn/15 text-warn" : "bg-success/15 text-success"
                    }`}
                  >
                    {pausada ? "En pausa" : "Activa"}
                  </span>
                </div>
                <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{pausada ? m.pausado : m.activo}</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="shrink-0"
                onClick={() => setConfirmar({ cual, pausar: !pausada })}
              >
                {pausada ? <PlayCircle size={12} /> : <PauseCircle size={12} />}
                {pausada ? "Reanudar" : "Pausar"}
              </Button>
            </div>
          );
        })}
      </div>

      <Dialog open={confirmar !== null} onOpenChange={(o) => !o && setConfirmar(null)}>
        <DialogContent>
          {confirmar && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {confirmar.pausar ? "Pausar" : "Reanudar"} {META[confirmar.cual].titulo.toLowerCase()}
                </DialogTitle>
                <DialogDescription>
                  {confirmar.pausar
                    ? `${META[confirmar.cual].pausado} Todo el equipo verá un aviso arriba mientras siga en pausa.`
                    : `${META[confirmar.cual].activo} Revisa que los datos estén listos antes de reanudar.`}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" size="sm" onClick={() => setConfirmar(null)}>Cancelar</Button>
                <Button size="sm" onClick={aplicar} disabled={updateMut.isPending}>
                  {updateMut.isPending && <Loader2 size={12} className="animate-spin" />}
                  {confirmar.pausar ? "Pausar" : "Reanudar"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
